import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { proxy } from "valtio";
import { getPointMaterial, POINT_SIZE } from "./annotations";
import { App } from "./app";
import type { DragEvent, DragStartEvent, InputEvent, ZoomEvent } from "./input-events";
import { Prefs } from "./prefs";
import { convertUnits, metersToScene } from "./utils";
import { type Viewer } from "./viewer";

const DARK_MODE_MEDIA_QUERY = window.matchMedia("(prefers-color-scheme: dark)");
const prefersDarkMode = () => DARK_MODE_MEDIA_QUERY.matches;

const DEFAULT_WORLD_WIDTH = 16;

const DEFAULT_ZOOM = 1;

const MIN_VIEW_WIDTH = 1;

const DEFAULT_PAN = new THREE.Vector2(0, 0);

export class CrossSection {
    public state = proxy({
        selectionId: null as string | null,
        depth: { value: 0, units: "m" },
        flipNormal: false,
        controlState: "idle" as "idle" | "pan",
        quality: "medium" as "low" | "medium" | "high",
        spacing: 0,
        changedAt: performance.now(),
    });

    public camera = new THREE.OrthographicCamera();
    public size = { width: 0, height: 0 };
    public scene: THREE.Scene;
    public overlay: THREE.Scene;
    public labels: THREE.Scene;
    public renderer: THREE.WebGLRenderer | null = null;
    public fallbackPlane = new THREE.Plane();

    private readonly viewer: Viewer;
    private readonly app: App;
    private readonly prefs: Prefs;

    private labelRenderer: CSS2DRenderer | null = null;
    private updateScheduled = false;

    private gridCamera!: THREE.OrthographicCamera;
    private gridMaterial!: THREE.LineBasicMaterial;
    private gridGeometry!: THREE.BufferGeometry;
    private gridMesh!: THREE.LineSegments;

    // Cross-section geometry
    private start: null | THREE.Vector3 = null;
    private end: null | THREE.Vector3 = null;
    private planeNormal = new THREE.Vector3();
    private clippingPlanes: THREE.Plane[] = [new THREE.Plane(), new THREE.Plane()];
    private markers = new THREE.Group();

    // Fit target for auto-framing
    private fitTargetSize = 128;
    private fitTarget = new THREE.WebGLRenderTarget(this.fitTargetSize, this.fitTargetSize);

    // Per-measurement state
    private worldSize = new Map<string, THREE.Vector2>();
    private pan = new Map<string, THREE.Vector2>();
    private zoom = new Map<string, number>();

    // Temp objects to avoid allocations
    private temp = {
        mid: new THREE.Vector3(),
        direction: new THREE.Vector3(),
        groundDirection: new THREE.Vector3(),
        cameraOffset: new THREE.Vector3(),
        planeNormal: new THREE.Vector3(),
        fwd: new THREE.Vector3(),
        right: new THREE.Vector3(),
        up: new THREE.Vector3(),
        startNdc: new THREE.Vector3(),
        endNdc: new THREE.Vector3(),
        startViewerNdc: new THREE.Vector3(),
        endViewerNdc: new THREE.Vector3(),
        sectionWidth: new THREE.Vector3(),
    };

    constructor(viewer: Viewer, app: App, prefs: Prefs) {
        this.viewer = viewer;
        this.app = app;
        this.prefs = prefs;

        if (this.prefs.state.units === "imperial") {
            Object.assign(this.state.depth, { value: 1, units: "ft" });
        } else {
            Object.assign(this.state.depth, { value: 10, units: "cm" });
        }

        this.overlay = new THREE.Scene();
        this.labels = new THREE.Scene();
        this.scene = new THREE.Scene();

        this.overlay.add(this.markers);
        this.scene.add(this.overlay);

        this.initCamera();
        this.initGridCamera();
        this.initGrid();

        DARK_MODE_MEDIA_QUERY.addEventListener("change", this.handleThemeChange);
        this.handleThemeChange({ matches: prefersDarkMode() });
    }

    private initCamera() {
        this.camera = new THREE.OrthographicCamera();
        this.camera.up.set(0, 0, 1);
        this.camera.near = 0.001;
        this.camera.far = this.depthInScene();
        this.camera.layers.enableAll();
    }

    private initGridCamera() {
        this.gridCamera = new THREE.OrthographicCamera();
        this.gridCamera.up.set(0, 0, 1);
        this.gridCamera.near = -1;
        this.gridCamera.far = 1;
        this.gridCamera.layers.enableAll();
    }

    private initGrid() {
        const maxGridLines = 2048;
        const gridPositions = new Float32Array(maxGridLines * 2 * 3);
        const color = new THREE.Color(0.7, 0.7, 0.7);
        const side = THREE.DoubleSide;

        this.gridMaterial = new THREE.LineBasicMaterial({
            color,
            side,
            depthWrite: false,
        });
        this.gridGeometry = new THREE.BufferGeometry();
        this.gridGeometry.setAttribute("position", new THREE.BufferAttribute(gridPositions, 3));
        this.gridMesh = new THREE.LineSegments(this.gridGeometry, this.gridMaterial);
        this.gridMesh.frustumCulled = false;
    }

    private handleThemeChange = (e: { matches: boolean }) => {
        const c = e.matches ? 0.1 : 0.7;
        this.gridMaterial.color.set(c, c, c);
        this.update();
    };

    disconnect() {
        this.renderer?.dispose();
        this.renderer = null;
        this.start = null;
        this.end = null;
        this.state.selectionId = null;
    }

    init(canvas: HTMLCanvasElement) {
        if (this.renderer) {
            return;
        }

        this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        this.renderer.clippingPlanes = [];

        this.labelRenderer = new CSS2DRenderer();
        this.labelRenderer.domElement.style.position = "absolute";
        this.labelRenderer.domElement.style.top = "0";
        this.labelRenderer.domElement.style.left = "0";
        this.labelRenderer.domElement.style.zIndex = "0";
        this.labelRenderer.domElement.style.pointerEvents = "none";
        canvas.parentElement?.appendChild(this.labelRenderer.domElement);
    }

    public setState(state: Partial<typeof this.state>) {
        Object.assign(this.state, state);
        this.update();
    }

    getMouseRay(position: THREE.Vector2): THREE.Ray {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(position, this.camera);
        return raycaster.ray;
    }

    public handleInput(event: InputEvent): boolean {
        let handled = false;

        switch (this.state.controlState) {
            case "idle":
                handled = this.handleIdleInput(event);
                break;
            case "pan":
                handled = this.handlePanInput(event);
                break;
        }

        if (handled) {
            this.state.changedAt = performance.now();
        }

        return handled;
    }

    private handleIdleInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag-start":
                return this.handleDragStart(event);
            case "zoom":
                return this.handleZoom(event);
        }
        return false;
    }

    private handlePanInput(event: InputEvent): boolean {
        switch (event.type) {
            case "drag":
                return this.handleDrag(event);
            case "drag-end":
                return this.handleDragEnd();
        }
        return false;
    }

    private handleDragStart(_event: DragStartEvent): boolean {
        this.state.controlState = "pan";
        return true;
    }

    private handleDrag(event: DragEvent): boolean {
        const measurementId = this.app.state.selectionId;

        if (!measurementId) {
            return false;
        }

        const zoom = this.getZoom(measurementId);
        const worldSize = this.getWorldSize(measurementId);

        if (!zoom || !worldSize) {
            return false;
        }

        const worldUnitsPerPixelX = worldSize.x / zoom / this.size.width;
        const worldUnitsPerPixelY = worldSize.y / zoom / this.size.height;

        this.panTo(
            event.originalEvent.movementX * worldUnitsPerPixelX,
            event.originalEvent.movementY * worldUnitsPerPixelY,
        );

        return true;
    }

    private handleDragEnd(): boolean {
        this.state.controlState = "idle";
        return true;
    }

    private handleZoom(event: ZoomEvent): boolean {
        const measurementId = this.app.state.selectionId;

        if (!measurementId) {
            return false;
        }

        const zoom = this.getZoom(measurementId);
        const pan = this.getPan(measurementId);
        const worldSize = this.getWorldSize(measurementId);

        if (!zoom || !pan || !worldSize) {
            return false;
        }

        const camera = this.camera;

        // Convert NDC position to screen space [0, 1]
        const xScreen = (event.position.x + 1) * 0.5;
        const yScreen = (1 - event.position.y) * 0.5;

        // Get world position before zoom
        const worldXBefore = camera.left + xScreen * (camera.right - camera.left);
        const worldYBefore = camera.top - yScreen * (camera.top - camera.bottom);

        // Apply zoom
        const zoomFactor = 1.5;
        const maxZoom = worldSize.x / MIN_VIEW_WIDTH;
        const newZoom = Math.min(maxZoom, Math.max(0.1, zoom * Math.pow(zoomFactor, event.delta)));
        this.setZoom(measurementId, newZoom);

        // Update camera planes with new zoom
        const halfWidth = worldSize.x / 2 / newZoom;
        const halfHeight = worldSize.y / 2 / newZoom;

        camera.left = -halfWidth + pan.x;
        camera.right = halfWidth + pan.x;
        camera.top = halfHeight + pan.y;
        camera.bottom = -halfHeight + pan.y;
        camera.updateProjectionMatrix();

        // Get world position after zoom
        const worldXAfter = camera.left + xScreen * (camera.right - camera.left);
        const worldYAfter = camera.top - yScreen * (camera.top - camera.bottom);

        // Compensate for position shift
        const dx = worldXAfter - worldXBefore;
        const dy = worldYAfter - worldYBefore;

        this.panTo(dx, -dy);

        return true;
    }

    /** The depth setting carries its own unit; the camera and clip planes live in scene space. */
    private depthInScene() {
        const meters = convertUnits(this.state.depth.value, this.state.depth.units, "m");
        return metersToScene(meters, this.app.state.sceneUnit);
    }

    setDepth(depth: typeof this.state.depth) {
        Object.assign(this.state.depth, depth);
        this.camera.far = this.depthInScene();
        this.camera.updateProjectionMatrix();
        this.update();
    }

    focus() {
        const measurementId = this.app.state.selectionId;
        if (!measurementId) return;

        this.setZoom(measurementId, 1);

        const pan = this.pan.get(measurementId);
        if (pan) pan.set(0, 0);

        this.fit();
    }

    connect(measurementId: string, start: THREE.Vector3Like, end: THREE.Vector3Like) {
        if (!this.zoom.has(measurementId)) {
            this.zoom.set(measurementId, 1);
        }

        if (!this.pan.has(measurementId)) {
            this.pan.set(measurementId, new THREE.Vector2(0, 0));
        }

        const worldStart = this.app.fromWGS84(start.x, start.y);
        const worldEnd = this.app.fromWGS84(end.x, end.y);

        const localStart = new THREE.Vector3(
            worldStart[0] - this.app.state.offset.x,
            worldStart[1] - this.app.state.offset.y,
            metersToScene(start.z, this.app.state.sceneUnit),
        );

        const localEnd = new THREE.Vector3(
            worldEnd[0] - this.app.state.offset.x,
            worldEnd[1] - this.app.state.offset.y,
            metersToScene(end.z, this.app.state.sceneUnit),
        );

        if (this.start && this.end) {
            const startDelta = this.start.distanceTo(localStart);
            const endDelta = this.end.distanceTo(localEnd);

            if (startDelta < 0.001 && endDelta < 0.001) {
                return false;
            }
        }

        this.start = localStart;
        this.end = localEnd;

        const depth = this.depthInScene();

        // First look at the middle of the cross section
        this.temp.mid.copy(this.start).add(this.end).divideScalar(2);
        this.temp.direction.copy(this.end).sub(this.start).normalize();
        this.planeNormal.set(-this.temp.direction.y, this.temp.direction.x, 0).normalize();
        this.temp.cameraOffset.copy(this.planeNormal).multiplyScalar(depth / 2);
        this.camera.position.copy(this.temp.mid).add(this.temp.cameraOffset);
        this.camera.lookAt(this.temp.mid);
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();

        // Determine if we need to flip the normal
        this.temp.startNdc.copy(this.start).project(this.camera);
        this.temp.endNdc.copy(this.end).project(this.camera);
        this.temp.startViewerNdc.copy(this.start).project(this.viewer.camera);
        this.temp.endViewerNdc.copy(this.end).project(this.viewer.camera);

        const shouldFlip =
            this.temp.startViewerNdc.x > this.temp.endViewerNdc.x !== this.temp.startNdc.x > this.temp.endNdc.x;

        if (shouldFlip !== this.state.flipNormal) {
            this.handleFlip();
        }

        this.fallbackPlane.setFromNormalAndCoplanarPoint(this.planeNormal, this.temp.mid);
        this.updateClippingPlanes();
        this.updateMarkers();
        return true;
    }

    setSize(width: number, height: number) {
        this.size.width = width;
        this.size.height = height;
        this.renderer?.setSize(width, height);
        this.labelRenderer?.setSize(width, height);

        const aspect = this.size.width / this.size.height;

        let worldSize = this.getWorldSize(this.app.state.selectionId);
        if (!worldSize) {
            worldSize = new THREE.Vector2(DEFAULT_WORLD_WIDTH, DEFAULT_WORLD_WIDTH / aspect);
        }

        this.updateCameraPlanes(worldSize, DEFAULT_ZOOM, DEFAULT_PAN);
        this.updateGrid();
        this.fit();
    }

    getWorldSize(measurementId: string | null) {
        return measurementId ? this.worldSize.get(measurementId) : undefined;
    }

    getPan(measurementId: string | null) {
        return measurementId ? this.pan.get(measurementId) : undefined;
    }

    getZoom(measurementId: string | null) {
        return measurementId ? this.zoom.get(measurementId) : undefined;
    }

    setZoom(measurementId: string, zoom: number) {
        this.zoom.set(measurementId, zoom);
    }

    panTo(dx: number, dy: number) {
        const measurementId = this.app.state.selectionId;

        if (!measurementId) {
            return;
        }

        const pan = this.pan.get(measurementId);
        const zoom = this.zoom.get(measurementId);
        const worldSize = this.getWorldSize(measurementId);

        if (!pan || !zoom || !worldSize) {
            return;
        }

        pan.x -= dx;
        pan.y += dy;
        this.updateCameraPlanes(worldSize, zoom, pan);
    }

    // Internal methods

    private updateCameraPlanes(worldSize: THREE.Vector2, zoom: number, pan: THREE.Vector2) {
        const halfWidth = worldSize.x / 2 / zoom;
        const halfHeight = worldSize.y / 2 / zoom;

        this.camera.left = -halfWidth + pan.x;
        this.camera.right = halfWidth + pan.x;
        this.camera.top = halfHeight + pan.y;
        this.camera.bottom = -halfHeight + pan.y;

        this.gridCamera.left = -halfWidth + pan.x;
        this.gridCamera.right = halfWidth + pan.x;
        this.gridCamera.top = halfHeight + pan.y;
        this.gridCamera.bottom = -halfHeight + pan.y;

        this.camera.updateProjectionMatrix();
        this.gridCamera.updateProjectionMatrix();

        this.updateGrid();
        this.update();
    }

    private updateClippingPlanes() {
        if (!this.start || !this.end) {
            return;
        }

        this.temp.groundDirection.copy(this.end).sub(this.start).setZ(0).normalize();
        this.clippingPlanes[0].setFromNormalAndCoplanarPoint(this.temp.groundDirection, this.start);
        this.temp.groundDirection.negate();
        this.clippingPlanes[1].setFromNormalAndCoplanarPoint(this.temp.groundDirection, this.end);
        this.temp.groundDirection.negate();
    }

    private getSpacing() {
        const worldWith = Math.max(this.gridCamera.right - this.gridCamera.left, 1);
        const worldHeight = Math.max(this.gridCamera.top - this.gridCamera.bottom, 1);
        const pixelsPerWorldUnitX = this.size.width / worldWith;
        const pixelsPerWorldUnitY = this.size.height / worldHeight;
        const pixelsPerWorldUnit = Math.min(pixelsPerWorldUnitX, pixelsPerWorldUnitY);
        const targetPixelStep = 32;
        const roughStep = targetPixelStep / pixelsPerWorldUnit;

        const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const normalized = roughStep / magnitude;
        let rounded;

        if (normalized <= 1) rounded = 1;
        else if (normalized <= 2) rounded = 2;
        else if (normalized <= 5) rounded = 5;
        else rounded = 10;

        return rounded * magnitude;
    }

    private updateGrid() {
        const spacing = this.getSpacing();
        const startX = Math.floor(this.gridCamera.left / spacing) * spacing;
        const endX = Math.ceil(this.gridCamera.right / spacing) * spacing;
        const startY = Math.floor(this.gridCamera.bottom / spacing) * spacing;
        const endY = Math.ceil(this.gridCamera.top / spacing) * spacing;
        const positions = this.gridGeometry.attributes.position.array as Float32Array;

        let i = 0;
        for (let x = startX; x <= endX; x += spacing) {
            positions[i++] = x;
            positions[i++] = this.gridCamera.bottom;
            positions[i++] = 0;

            positions[i++] = x;
            positions[i++] = this.gridCamera.top;
            positions[i++] = 0;
        }

        for (let y = startY; y <= endY; y += spacing) {
            positions[i++] = this.gridCamera.left;
            positions[i++] = y;
            positions[i++] = 0;

            positions[i++] = this.gridCamera.right;
            positions[i++] = y;
            positions[i++] = 0;
        }

        this.gridGeometry.setDrawRange(0, i / 3);
        this.gridGeometry.attributes.position.needsUpdate = true;
        this.state.spacing = spacing;
    }

    private updateMarkers() {
        this.markers.clear();

        if (!this.start || !this.end) {
            return;
        }

        const startMaterial = getPointMaterial(POINT_SIZE, 1, "#82DF30");
        const endMaterial = getPointMaterial(POINT_SIZE, 1, "#FF5C26");

        const startPointGeometry = new THREE.BufferGeometry();
        startPointGeometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0], 3));
        const startPoint = new THREE.Points(startPointGeometry, startMaterial);
        startPoint.position.copy(this.start);
        this.markers.add(startPoint);

        const endPointGeometry = new THREE.BufferGeometry();
        endPointGeometry.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0], 3));
        const endPoint = new THREE.Points(endPointGeometry, endMaterial);
        endPoint.position.copy(this.end);
        this.markers.add(endPoint);
    }

    fit() {
        const measurementId = this.app.state.selectionId;

        if (!measurementId || !this.renderer || !this.start || !this.end) {
            return;
        }

        const zoom = this.zoom.get(measurementId);
        const pan = this.pan.get(measurementId);

        if (!zoom || !pan) {
            return;
        }

        // Calculate world size
        this.temp.sectionWidth.copy(this.end).sub(this.start).setZ(0);
        const sectionWidth = this.temp.sectionWidth.length();
        const scanSize = Math.max(this.fitTargetSize / 4, sectionWidth);

        this.camera.left = -scanSize / 2;
        this.camera.right = scanSize / 2;
        this.camera.top = scanSize / 2;
        this.camera.bottom = -scanSize / 2;
        this.camera.updateProjectionMatrix();

        // Render the scene to the render target for auto-framing
        this.renderer.clippingPlanes = this.clippingPlanes;
        this.renderer.setRenderTarget(this.fitTarget);
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
        this.renderer.setRenderTarget(null);
        this.renderer.clippingPlanes = [];

        // Find the visible content bounds
        const rgba = new Uint8Array(4 * this.fitTargetSize * this.fitTargetSize);
        this.renderer.readRenderTargetPixels(this.fitTarget, 0, 0, this.fitTargetSize, this.fitTargetSize, rgba);

        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;

        for (let i = 0; i < rgba.length; i += 4) {
            const r = rgba[i];
            const g = rgba[i + 1];
            const b = rgba[i + 2];
            const a = rgba[i + 3];

            if (r > 0 || g > 0 || b > 0 || a > 0) {
                const pixelIndex = i / 4;
                const x = pixelIndex % this.fitTargetSize;
                const y = Math.floor(pixelIndex / this.fitTargetSize);

                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }

        const windowAspect = this.size.width / this.size.height;
        const contentPadding = 1.1;

        // Adjust the camera position and world size to fit the content if visible
        if (isFinite(minX) && isFinite(maxX) && isFinite(minY) && isFinite(maxY)) {
            const wuPerPxY = scanSize / this.fitTargetSize;
            const wuPerPxX = scanSize / this.fitTargetSize;
            const pixelHeight = maxY - minY;
            const pixelWidth = maxX - minX;

            let contentHeight = pixelHeight * wuPerPxY * contentPadding;
            let contentWidth = Math.max(pixelWidth * wuPerPxX, sectionWidth) * contentPadding;

            const contentAspect = contentWidth / contentHeight;

            if (contentAspect > windowAspect) {
                contentHeight = contentWidth / windowAspect;
            } else {
                contentWidth = contentHeight * windowAspect;
            }

            const contentCenterY = (minY + maxY) / 2;
            const pixelOffsetY = contentCenterY - this.fitTargetSize / 2;
            const worldOffsetY = pixelOffsetY * wuPerPxY;

            this.camera.getWorldDirection(this.temp.fwd);
            this.temp.right.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
            this.temp.up.crossVectors(this.temp.right, this.temp.fwd).normalize();

            this.camera.position.addScaledVector(this.temp.up, worldOffsetY);
            this.camera.lookAt(this.temp.mid.addScaledVector(this.temp.up, worldOffsetY));

            const worldSize = new THREE.Vector2(contentWidth, contentHeight);

            this.worldSize.set(measurementId, worldSize);
            this.updateCameraPlanes(worldSize, zoom, pan);
        }
    }

    private handleFlip() {
        const depth = this.depthInScene();
        this.planeNormal.negate();
        this.camera.position.addScaledVector(this.planeNormal, depth);
        this.camera.lookAt(this.temp.mid);
    }

    public flip() {
        this.state.flipNormal = !this.state.flipNormal;
        this.handleFlip();
        this.fallbackPlane.negate();
        this.fit();
    }

    public update() {
        if (!this.updateScheduled) {
            this.updateScheduled = true;

            requestAnimationFrame(() => {
                this.updateScheduled = false;

                if (!this.renderer || !this.labelRenderer) {
                    return;
                }

                // Clear and render the grid in the background
                this.renderer.autoClear = true;
                this.renderer.render(this.gridMesh, this.gridCamera);
                this.renderer.autoClear = false;

                // Render the points inside the clipping planes
                this.renderer.clippingPlanes = this.clippingPlanes;
                this.renderer.render(this.scene, this.camera);
                this.renderer.clippingPlanes = [];

                // Avoid clipping annotations when depth is small
                const far = this.camera.far;
                this.camera.far = 1000;
                this.camera.updateProjectionMatrix();
                this.renderer.render(this.overlay, this.camera);
                this.labelRenderer.render(this.labels, this.camera);
                this.camera.far = far;
                this.camera.updateProjectionMatrix();
            });
        }
    }
}
